import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PlinthionLogo from '../components/PlinthionLogo';

const LandingPage = () => {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * -20,
    }))
  );

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: '#0c1452' }}>
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(78,205,196,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(78,205,196,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.id % 3 === 0 ? '#4ECDC4' : 'rgba(255,255,255,0.3)',
            animation: `landing-float ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}

      {/* Radial glow behind logo */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: '700px',
          height: '700px',
          background: 'radial-gradient(circle, rgba(78,205,196,0.08) 0%, rgba(22,32,123,0) 70%)',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
        {/* Logo */}
        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
          }}
        >
          <div className="flex justify-center mb-8">
            <PlinthionLogo size="xlarge" light={true} />
          </div>
        </div>

        {/* Tagline */}
        <div
          className="transition-all duration-1000 ease-out delay-300"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '300ms',
          }}
        >
          <p className="font-heading text-lg md:text-xl text-white/60 tracking-widest uppercase mb-8">
            B2B Logistics Marketplace
          </p>
          <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-black text-white leading-tight max-w-2xl mx-auto">
            Build Your Supply Chain on the Plinthion
          </h2>
        </div>

        {/* Decorative line */}
        <div
          className="my-10 transition-all duration-1000 ease-out"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'scaleX(1)' : 'scaleX(0)',
            transitionDelay: '600ms',
          }}
        >
          <div className="w-24 h-[2px] mx-auto" style={{ backgroundColor: '#4ECDC4' }} />
        </div>

        {/* CTA Button */}
        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '800ms',
          }}
        >
          <button
            onClick={() => navigate('/login')}
            data-testid="unlock-plinthion-btn"
            className="group relative px-10 py-4 font-heading font-bold text-lg tracking-wide text-white rounded-sm overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(78,205,196,0.3)] active:scale-95 cursor-pointer"
            style={{ backgroundColor: '#16207B', border: '2px solid #4ECDC4' }}
          >
            <span className="relative z-10 flex items-center gap-3">
              Unlock the Plinthion
              <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: 'linear-gradient(135deg, #16207B 0%, #1a2a8f 50%, #16207B 100%)' }}
            />
          </button>
        </div>

        {/* Service pills */}
        <div
          className="mt-16 flex flex-wrap justify-center gap-3 max-w-xl transition-all duration-1000 ease-out"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(15px)',
            transitionDelay: '1100ms',
          }}
        >
          {['Warehouse Space', 'Material Handling Equipment', 'Fleet & Transport', 'Skilled Manpower'].map((s) => (
            <span
              key={s}
              className="px-4 py-1.5 text-xs font-medium tracking-wide uppercase rounded-full border"
              style={{ color: '#4ECDC4', borderColor: 'rgba(78,205,196,0.25)', backgroundColor: 'rgba(78,205,196,0.06)' }}
            >
              {s}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div
          className="mt-16 mb-6 text-center transition-all duration-1000 ease-out"
          style={{
            opacity: loaded ? 1 : 0,
            transitionDelay: '1400ms',
          }}
        >
          <p className="text-xs text-white tracking-wide">
            &copy; {new Date().getFullYear()} Plinthion Ltd. All Rights Reserved.
          </p>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes landing-float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.4; }
          25% { transform: translateY(-30px) translateX(10px); opacity: 0.8; }
          50% { transform: translateY(-15px) translateX(-10px); opacity: 0.5; }
          75% { transform: translateY(-40px) translateX(5px); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
